"use client";

import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area";
import type React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export function ScrollArea({
  className,
  children,
  scrollFade = false,
  scrollbarGutter = false,
  fill = false,
  clampContentMinWidth = true,
  ...props
}: ScrollAreaPrimitive.Root.Props & Omittable<{
  scrollFade?: boolean;
  /**
   * Reserve the vertical scrollbar's lane so content ends before the bar
   * instead of running under it. `true` reserves it only while the content
   * actually overflows; `"stable"` reserves it always, for a host that uses
   * the lane as its own right gutter and must not reflow when the list
   * crosses the overflow threshold.
   */
  scrollbarGutter?: boolean | "stable";
  fill?: boolean;
  clampContentMinWidth?: boolean;
}>): React.ReactElement {
  return (
    // The root is a flex column and the viewport is its flex child ON PURPOSE.
    // The viewport is the element that scrolls, so it must end up with a height
    // that is smaller than its content. `h-full` cannot deliver that: a
    // percentage height needs a *definite* base, and almost every container a
    // scroll area lives in — dialog popup, sheet, drawer, combobox list,
    // selector popover — is `height: auto` bounded by `max-height`. A max does
    // not make a height definite (CSS Flexbox 9.8), so `h-full` degraded to
    // `auto`, the viewport grew to its content, `overflow: scroll` had nothing
    // to scroll, and the content silently spilled and got clipped. The flex
    // algorithm has no such requirement: `flex-1` + `min-h-0` gives the
    // viewport exactly the root's content box whether that box was sized by a
    // percentage, by a max-height clamp, or by shrinking under a sibling.
    //
    // Same reasoning one level up: `flex-1` lets the root claim the leftover
    // space in a flex column instead of asking for 100% of the column and
    // squashing its siblings proportionally. `h-full` stays for the case flex
    // cannot cover — a lone child of a definite-height block parent.
    <ScrollAreaPrimitive.Root
      className={cn("flex h-full w-full min-h-0 flex-1 flex-col", className)}
      {...props}
    >
      {/* Keyboard focus ring: INSET, because a viewport filling a dialog
          body edge to edge has its sides under the popup's overflow clip —
          an offset ring survives only as a line above and below. And only
          while the viewport is tabbable: Base UI stamps `tabindex="-1"` on
          one that does not scroll, so a programmatic fallback focus (the
          focused control was just removed) never paints a ring on a body
          nobody can scroll. */}
      <ScrollAreaPrimitive.Viewport
        className={cn(
          "min-h-0 flex-1 rounded-[inherit] outline-none transition-shadows focus-visible:not-[[tabindex='-1']]:ring-2 focus-visible:not-[[tabindex='-1']]:ring-inset focus-visible:not-[[tabindex='-1']]:ring-ring data-has-overflow-y:overscroll-y-contain data-has-overflow-x:overscroll-x-contain",
          scrollFade &&
            "mask-t-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-y-start)))] mask-b-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-y-end)))] mask-l-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-x-start)))] mask-r-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-x-end)))] [--fade-size:1.5rem]",
          scrollbarGutter === "stable"
            ? "pe-2.5 data-has-overflow-x:pb-2.5"
            : scrollbarGutter &&
                "data-has-overflow-y:pe-2.5 data-has-overflow-x:pb-2.5",
        )}
        data-slot="scroll-area-viewport"
      >
        <ScrollAreaPrimitive.Content
          className={cn(fill && "size-full")}
          data-slot="scroll-area-content"
          style={clampContentMinWidth ? { minWidth: 0 } : undefined}
        >
          {children}
        </ScrollAreaPrimitive.Content>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar orientation="vertical" />
      <ScrollBar orientation="horizontal" />
      <ScrollAreaPrimitive.Corner data-slot="scroll-area-corner" />
    </ScrollAreaPrimitive.Root>
  );
}

export function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: ScrollAreaPrimitive.Scrollbar.Props): React.ReactElement {
  return (
    <ScrollAreaPrimitive.Scrollbar
      className={cn(
        "m-1 flex opacity-0 transition-opacity delay-300 data-[orientation=horizontal]:h-1.5 data-[orientation=vertical]:w-1.5 data-[orientation=horizontal]:flex-col data-hovering:opacity-100 data-scrolling:opacity-100 data-hovering:delay-0 data-scrolling:delay-0 data-hovering:duration-fast data-scrolling:duration-fast",
        className,
      )}
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        className="relative flex-1 rounded-full bg-foreground/20"
        data-slot="scroll-area-thumb"
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}
